import React, { useState, useEffect } from 'react';
import { activityAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import './ActivityManagement.css';

const ActivityManagement = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingActivity, setEditingActivity] = useState(null);
  const [currentActivity, setCurrentActivity] = useState({
    activityId: 0,
    activityName: '',
    enrolYear: new Date().getFullYear(),
    activityDescription: '',
    status: 'Active',
    sizeConfig: {
      activityId: 0,
      enableUniformSizes: false,
      uniformSizes: 'XS,S,M,L,XL,XXL,XXXL',
      enableShoeSizes: false,
      shoeSizes: '12,13,1,2,3,4,5,6,7,8,9,10'
    }
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();

  // Size options for toggle buttons
  const UNIFORM_SIZE_OPTIONS = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const SHOE_SIZE_OPTIONS = ['12', '13', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];

  useEffect(() => {
    loadActivities();
  }, [currentPage, pageSize, searchTerm]);

  const loadActivities = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await activityAPI.getActivities(currentPage, pageSize, searchTerm);
      if (response.success) {
        setActivities(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
      } else {
        setError(response.message || 'Failed to load activities');
      }
    } catch (err) {
      console.error('Load error:', err);
      setError('An error occurred while loading activities');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateActivity = () => {
    setModalMode('create');
    setEditingActivity(null);
    setCurrentActivity({
      activityId: 0,
      activityName: '',
      enrolYear: new Date().getFullYear(),
      activityDescription: '',
      status: 'Active',
      sizeConfig: {
        activityId: 0,
        enableUniformSizes: false,
        uniformSizes: 'XS,S,M,L,XL,XXL,XXXL',
        enableShoeSizes: false,
        shoeSizes: '12,13,1,2,3,4,5,6,7,8,9,10'
      }
    });
    setShowModal(true);
  };

  const handleEditActivity = (activity) => {
    setModalMode('edit');
    setEditingActivity(activity);
    setCurrentActivity({
      activityId: activity.activityId,
      activityName: activity.activityName,
      enrolYear: activity.enrolYear,
      activityDescription: activity.activityDescription || '',
      status: activity.status || 'Active',
      sizeConfig: activity.sizeConfig || {
        activityId: activity.activityId,
        enableUniformSizes: false,
        uniformSizes: 'XS,S,M,L,XL,XXL,XXXL',
        enableShoeSizes: false,
        shoeSizes: '12,13,1,2,3,4,5,6,7,8,9,10'
      }
    });
    setShowModal(true);
  };

  const handleDeleteActivity = async (activityId) => {
    if (window.confirm('Are you sure you want to delete this activity?')) {
      try {
        const response = await activityAPI.deleteActivity(activityId);
        if (response.success) {
          loadActivities();
          console.log('Activity deleted successfully');
        } else {
          setError(response.message || 'Failed to delete activity');
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('An error occurred while deleting the activity');
      }
    }
  };

  const handleManageSchools = (activity) => {
    navigate(`/activity/${activity.activityId}/schools`);
  };

  // Toggle individual size selection
  const toggleUniformSize = (size) => {
    const currentSizes = (currentActivity.sizeConfig?.uniformSizes || '').split(',').map(s => s.trim()).filter(s => s);
    const sizeIndex = currentSizes.indexOf(size);

    let updatedSizes;
    if (sizeIndex > -1) {
      // Remove size
      updatedSizes = currentSizes.filter(s => s !== size);
    } else {
      // Add size
      updatedSizes = [...currentSizes, size];
    }

    handleSizeConfigChange('uniformSizes', updatedSizes.join(','));
  };

  const toggleShoeSize = (size) => {
    const currentSizes = (currentActivity.sizeConfig?.shoeSizes || '').split(',').map(s => s.trim()).filter(s => s);
    const sizeIndex = currentSizes.indexOf(size);

    let updatedSizes;
    if (sizeIndex > -1) {
      // Remove size
      updatedSizes = currentSizes.filter(s => s !== size);
    } else {
      // Add size
      updatedSizes = [...currentSizes, size];
    }

    handleSizeConfigChange('shoeSizes', updatedSizes.join(','));
  };

  // Check if a size is selected
  const isUniformSizeSelected = (size) => {
    const currentSizes = (currentActivity.sizeConfig?.uniformSizes || '').split(',').map(s => s.trim()).filter(s => s);
    return currentSizes.includes(size);
  };

  const isShoeSizeSelected = (size) => {
    const currentSizes = (currentActivity.sizeConfig?.shoeSizes || '').split(',').map(s => s.trim()).filter(s => s);
    return currentSizes.includes(size);
  };

  const handleSaveActivity = async () => {
    try {
      let response;
      if (modalMode === 'create') {
        response = await activityAPI.createActivity({
          activityName: currentActivity.activityName,
          enrolYear: currentActivity.enrolYear,
          activityDescription: currentActivity.activityDescription,
          status: currentActivity.status,
          sizeConfig: currentActivity.sizeConfig
        });
      } else {
        response = await activityAPI.updateActivity(currentActivity.activityId, {
          ...currentActivity,
          id: currentActivity.activityId // Match required field name
        });
      }

      if (response.success) {
        setShowModal(false);
        loadActivities();
        console.log(`${modalMode === 'create' ? 'Created' : 'Updated'} activity successfully`);
      } else {
        setError(response.message || `Failed to ${modalMode} activity`);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(`An error occurred while ${modalMode === 'create' ? 'creating' : 'updating'} the activity`);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSizeConfigChange = (field, value) => {
    setCurrentActivity(prev => ({
      ...prev,
      sizeConfig: {
        ...prev.sizeConfig,
        [field]: value
      }
    }));
  };

  if (loading && !activities.length) {
    return (
      <div className="activity-management">
        <div className="loading">Loading activities...</div>
      </div>
    );
  }

  return (
    <div className="activity-management">
      <div className="activity-management-header">
        <div className="header-info">
          <h2><i className="fas fa-tasks"></i> Activity Management</h2>
          <p>Manage uniform collection activities and campaigns</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="activity-management-toolbar">
        <div className="search-section">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search activities..."
            className="search-input"
          />
          <button onClick={handleSearch} className="btn-search">
            <i className="fas fa-search"></i> Search
          </button>
        </div>
        <button onClick={handleCreateActivity} className="btn-create">
          <i className="fas fa-plus"></i> Add New Activity
        </button>
      </div>

      <div className="table-container">
        <table className="master-data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Activity Name</th>
              <th>Year</th>
              <th>Status</th>
              <th>School Count</th>
              <th>Created Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {activities.length > 0 ? (
              activities.map((activity, index) => (
                <tr key={activity.activityId}>
                  <td>{index + 1 + (currentPage - 1) * pageSize}</td>
                  <td><strong>{activity.activityName}</strong></td>
                  <td><span className="badge badge-primary">{activity.enrolYear}</span></td>
                  <td>
                    <span className={`badge ${
                      activity.status?.toUpperCase() === 'ACTIVE' ? 'badge-success' : 'badge-secondary'
                    }`}>
                      {activity.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    <span className="badge badge-info">{activity.schoolCount || 0} schools</span>
                  </td>
                  <td>
                    {activity.createdDate ? new Date(activity.createdDate).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="actions-column">
                    <button
                      onClick={() => handleEditActivity(activity)}
                      className="btn-action btn-edit"
                      title="Edit Activity"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleManageSchools(activity)}
                      className="btn-action btn-manage"
                      title="Manage Schools"
                    >
                      <i className="fas fa-school"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteActivity(activity.activityId)}
                      className="btn-action btn-delete"
                      title="Delete Activity"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data">No activities found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({totalCount} total activities)
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              First
            </button>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              Previous
            </button>
            <span className="page-input">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => goToPage(Number(e.target.value))}
                min="1"
                max={totalPages}
                className="page-number-input"
              />
              <span>of {totalPages}</span>
            </span>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Next
            </button>
            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Last
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      {/* Modal for Create/Edit Activity */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add New Activity' : `Edit Activity: ${currentActivity.activityName}`}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="activityName">Activity Name *</label>
                <input
                  id="activityName"
                  type="text"
                  value={currentActivity.activityName}
                  onChange={(e) =>
                    setCurrentActivity({ ...currentActivity, activityName: e.target.value.trim() })
                  }
                  placeholder="Enter activity name (e.g., KTC Uniform 2026)"
                  maxLength={255}
                  className="form-control"
                />
                <small className="form-help">Descriptive name for the campaign</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="enrolYear">Enrollment Year *</label>
                  <input
                    id="enrolYear"
                    type="number"
                    value={currentActivity.enrolYear}
                    onChange={(e) =>
                      setCurrentActivity({ ...currentActivity, enrolYear: parseInt(e.target.value) || new Date().getFullYear() })
                    }
                    placeholder="Enter year (2020-2100)"
                    min={2020}
                    max={2100}
                    className="form-control"
                  />
                  <small className="form-help">Which year to collect for</small>
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status *</label>
                  <select
                    id="status"
                    value={currentActivity.status}
                    onChange={(e) =>
                      setCurrentActivity({ ...currentActivity, status: e.target.value })
                    }
                    className="form-control"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="activityDescription">Description</label>
                <textarea
                  id="activityDescription"
                  value={currentActivity.activityDescription}
                  onChange={(e) =>
                    setCurrentActivity({ ...currentActivity, activityDescription: e.target.value.trim() })
                  }
                  placeholder="Enter activity description (optional)"
                  maxLength={500}
                  rows={3}
                  className="form-control"
                />
              </div>

              <div className="section-divider">
                <h4><i className="fas fa-ruler"></i> Size Configuration</h4>
                <p className="section-subtitle">Configure uniform and shoe sizes for this activity</p>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={currentActivity.sizeConfig?.enableUniformSizes}
                    onChange={(e) => handleSizeConfigChange('enableUniformSizes', e.target.checked)}
                    className="checkbox-input"
                  />
                  Enable Uniform Size Selection
                </label>
              </div>

              {currentActivity.sizeConfig?.enableUniformSizes && (
                <div className="form-group">
                  <label>Select Uniform Sizes:</label>
                  <div className="size-button-grid">
                    {UNIFORM_SIZE_OPTIONS.map(size => (
                      <button
                        key={size}
                        type="button"
                        className={`size-toggle-btn ${isUniformSizeSelected(size) ? 'selected' : 'unselected'}`}
                        onClick={() => toggleUniformSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  <small className="form-help">Click to toggle sizes. Orange = not selected, Green = selected</small>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={currentActivity.sizeConfig?.enableShoeSizes}
                    onChange={(e) => handleSizeConfigChange('enableShoeSizes', e.target.checked)}
                    className="checkbox-input"
                  />
                  Enable Shoe Size Selection
                </label>
              </div>

              {currentActivity.sizeConfig?.enableShoeSizes && (
                <div className="form-group">
                  <label>Select Shoe Sizes:</label>
                  <div className="size-button-grid">
                    {SHOE_SIZE_OPTIONS.map(size => (
                      <button
                        key={size}
                        type="button"
                        className={`size-toggle-btn ${isShoeSizeSelected(size) ? 'selected' : 'unselected'}`}
                        onClick={() => toggleShoeSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  <small className="form-help">Click to toggle sizes. Orange = not selected, Green = selected</small>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveActivity}
                disabled={!currentActivity.activityName.trim()}
                className="btn btn-primary"
              >
                {modalMode === 'create' ? 'Create Activity' : 'Update Activity'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityManagement;